import React, {
    createRef,
    FunctionComponent,
    KeyboardEventHandler,
    MouseEvent as ReactMouseEvent,
    MouseEventHandler,
    useContext,
} from "react";

import {
    AlignItems,
    FlexDirection,
    FlexGrow,
    Icon,
    JustifyContent,
    Layout,
    SpacingSize,
} from "@multiweb/reactComponents";
import { bemClassNames, createQA, TAB_INDEX_FOCUSABLE_ONLY, useSetFocus } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { QAProps } from "@multiweb/types/react";
import { MENU_PANEL_FOOTER_HEIGHT } from "../../constants";
import { ScrollContext } from "../../context";
import {
    createActivationKeyHandler,
    createActivationMouseHandler,
    createMenuCustomScroll,
} from "../../utils";

export type MenuPanelItemComponentProps = {
    /** Kód zobrazené ikony */
    readonly iconCode?: IconCode;
    /** Příznak, zda je prvek aktivní - výchozí hodnota je false */
    readonly isActive?: boolean;
    /** Příznak, zda je prvek zvýrazněný - výchozí hodnota je false */
    readonly isHighlighted?: boolean;
    /** Callback volaný při kliknutí. Reaguje na primární i ostatní tlačítka myši (přidává se na onClick i onAuxClick). */
    readonly onClick: MouseEventHandler<HTMLDivElement>;
    /** Callback volaný při uvolnění klávesy */
    readonly onKeyUp?: KeyboardEventHandler<HTMLDivElement>;
    /** Popisek zobrazený po najetí kurzoru */
    readonly title?: string;
    /** Příznak, zda je prvek zaměřený */
    readonly isFocused: boolean;
    /** Popisek zobrazený pod ikonou */
    readonly caption?: string;
    /** Příznak, zda má prvek větší výšku */
    readonly higher?: boolean;
    /** Příznak, zda má být zobrazena rozbalovací šipka. */
    readonly isExpandArrowVisible: boolean;
    /** Příznak, zda-li zobrazit oddělovací čáru mezi ikonkou menu a rozbalovací šipkou. (Má vliv pouze pro `isExpandArrowVisible === true`.) */
    readonly showSeparator?: boolean;
} & QAProps;

const classNames = bemClassNames("menu-panel-item-component");

/** Lokální jméno CSS třídy (bez BEM prefixů) reprezentující prvek s rozbalovací šipkou */
const EXPAND_ARROW_CLASS_NAME_BASE = "right-part-wrapper";
/** CSS třída reprezentující prvek s rozbalovací šipkou. Používá se v {@link isRightPartEvent}. */
const EXPAND_ARROW_CLASS_NAME = classNames(EXPAND_ARROW_CLASS_NAME_BASE);
/** Regulární výraz pro aplikaci na HTML atribut `class`. Matchuje, pokud daný atribut obsahuje také {@link EXPAND_ARROW_CLASS_NAME}. */
const EXPAND_ARROW_CLASS_NAME_REGEXP = new RegExp(`(^| )${EXPAND_ARROW_CLASS_NAME}($| )`);

/** Tlačítko určené pro změnu sekce v bočním panelu menu, umožňuje zobrazení zvýrazňujícího prvku */
export const MenuPanelItemComponent: FunctionComponent<MenuPanelItemComponentProps> = ({
    iconCode,
    isActive = false,
    isHighlighted = false,
    onClick,
    title,
    isFocused,
    onKeyUp,
    caption,
    higher = false,
    isExpandArrowVisible,
    showSeparator,
    qa,
}) => {
    const ref = createRef<HTMLDivElement>();
    const scroll = useContext(ScrollContext);
    useSetFocus<HTMLDivElement>(isFocused, ref, createMenuCustomScroll(scroll, MENU_PANEL_FOOTER_HEIGHT));
    const handleClick = createActivationMouseHandler(onClick);
    const handleKeyUp = createActivationKeyHandler(onKeyUp);
    return (
        <div
            role="button" //Nemůže být button element, protože obsahuje layout, který není inline typ elementu
            className={classNames({
                "hover-separated-panel": showSeparator,
                "hover-panel": !showSeparator,
                higher,
                active: isActive,
                fullwidth: true,
            })}
            tabIndex={TAB_INDEX_FOCUSABLE_ONLY}
            onClick={handleClick}
            onAuxClick={handleClick}
            onKeyUp={handleKeyUp}
            {...createQA(qa)}
            {...{ title, ref }}
        >
            <Layout
                flexDirection={FlexDirection.row}
                flexGrow={FlexGrow.one}
                justifyContent={JustifyContent.spaceBetween}
            >
                <div className={classNames({ "hover-panel": showSeparator })}>
                    <Layout
                        alignItems={AlignItems.center}
                        justifyContent={JustifyContent.center}
                        flexDirection={FlexDirection.column}
                        spacingSizeLeft={SpacingSize.m}
                        spacingSizeRight={isExpandArrowVisible || caption ? SpacingSize.none : SpacingSize.m}
                        flexGrow={FlexGrow.one}
                    >
                        <span className={classNames("stripe", { active: isActive })} />
                        {iconCode && (
                            <div className={classNames("icon-wrapper")}>
                                <Icon iconCode={iconCode} />
                                <span className={classNames("highlight-dot", { active: isHighlighted })} />
                            </div>
                        )}
                        {caption && <span className={classNames("caption")}>{caption}</span>}
                    </Layout>
                </div>
                {isExpandArrowVisible ? (
                    <div className={classNames(EXPAND_ARROW_CLASS_NAME_BASE, { showSeparator })}>
                        <Icon iconCode={IconCode.uiChevronRightSmall} />
                    </div>
                ) : (
                    caption && <Layout spacingSize={SpacingSize.xs} />
                )}
            </Layout>
        </div>
    );
};

/**
 * Určí, zda událost pochází z elementů rozbalovací šipky. (Určeno pro potřeby kombinovaného tlačítka.)
 * @param event Událost pro kontrolu.
 */
export const isRightPartEvent = (event: ReactMouseEvent): boolean => {
    if (!(event.target instanceof HTMLElement)) {
        return false;
    }

    const targetElement: HTMLElement = event.target;
    return (
        EXPAND_ARROW_CLASS_NAME_REGEXP.test(targetElement.className) ||
        EXPAND_ARROW_CLASS_NAME_REGEXP.test(targetElement.parentElement?.className ?? "")
    );
};
